import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { beforeAfterProjects } from '../../../mocks/projects';
import Lightbox from '../../../components/feature/Lightbox';

const categories = [
  { key: 'all', label: 'Alle Projecten', icon: 'ri-grid-line' },
  { key: 'badkamer', label: 'Badkamer', icon: 'ri-drop-line' },
  { key: 'keuken', label: 'Keuken', icon: 'ri-restaurant-line' },
  { key: 'appartement', label: 'Appartement', icon: 'ri-building-line' },
];

const PhotosGallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') ?? 'all';
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [lightbox, setLightbox] = useState<{ images: { src: string; alt: string; label?: string }[]; index: number; title: string } | null>(null);

  useEffect(() => {
    const cat = searchParams.get('category') ?? 'all';
    setActiveCategory(cat);
  }, [searchParams]);

  const handleCategoryChange = (key: string) => {
    setActiveCategory(key);
    if (key === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: key });
    }
  };

  const filtered = activeCategory === 'all'
    ? beforeAfterProjects
    : beforeAfterProjects.filter((p) => p.category === activeCategory);

  const openLightbox = (projectIndex: number, imageIndex: number) => {
    const project = filtered[projectIndex];
    setLightbox({
      title: project.location,
      index: imageIndex,
      images: [
        { src: project.beforeImage, alt: `Voor - ${project.location}`, label: 'Voor' },
        { src: project.afterImage, alt: `Na - ${project.location}`, label: 'Na' },
      ],
    });
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-10">
          <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
            Voor &amp; Na Transformaties
          </h2>
          <p className="text-gray-600">
            Zie het verschil dat vakmanschap en aandacht voor detail maken
          </p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => handleCategoryChange(cat.key)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeCategory === cat.key
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeCategory === cat.key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {cat.key === 'all' ? beforeAfterProjects.length : beforeAfterProjects.filter(p => p.category === cat.key).length}
              </span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <i className="ri-image-line text-5xl mb-4 block"></i>
            <p className="text-lg">Geen projecten gevonden in deze categorie.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {filtered.map((project, projectIndex) => (
              <div
                key={project.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer group"
              >
                <div className="grid grid-cols-2 divide-x divide-gray-200">
                  {/* Voor */}
                  <div
                    className="relative aspect-[3/4] overflow-hidden"
                    onClick={() => openLightbox(projectIndex, 0)}
                  >
                    <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">Voor</span>
                    </div>
                    <img
                      src={project.beforeImage}
                      alt={`Voor - ${project.location}`}
                      className="w-full h-full object-cover object-top grayscale group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/0 group-hover:bg-white/80 transition-all duration-300 opacity-0 group-hover:opacity-100">
                        <i className="ri-zoom-in-line text-gray-900 text-lg"></i>
                      </div>
                    </div>
                  </div>

                  {/* Na */}
                  <div
                    className="relative aspect-[3/4] overflow-hidden"
                    onClick={() => openLightbox(projectIndex, 1)}
                  >
                    <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">Na</span>
                    </div>
                    <img
                      src={project.afterImage}
                      alt={`Na - ${project.location}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/0 group-hover:bg-white/80 transition-all duration-300 opacity-0 group-hover:opacity-100">
                        <i className="ri-zoom-in-line text-gray-900 text-lg"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 text-center">
                  <p className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-1"></i>
                    {project.location}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Klik om te vergroten</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-gray-50 rounded-3xl p-12 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'serif' }}>
            Wilt u uw project hier zien?
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Neem contact met ons op en laten we samen uw droomruimte creëren
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg text-base font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Start Uw Project
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          title={lightbox.title}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
};

export default PhotosGallery;
